import type { AtualizarPerfil } from "@base/contracts";
import type {
  AtualizarUsuario,
  Credenciais,
  ConsultaDeUsuarios,
  CriarUsuario,
  RedefinirSenha,
} from "@base/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { marcarLoginRecente } from "./login-recente";
import { esquecerSessaoConhecida, lembrarSessaoConhecida } from "./sessao-conhecida";
import { useUsuariosGateway } from "./usuarios.contexto";

/**
 * Casos de uso do front, um hook cada. Chaves de cache centralizadas para a
 * invalidacao nao depender de alguem lembrar a string exata.
 */
export const chavesDeUsuarios = {
  sessao: ["sessao"] as const,
  entrar: ["usuarios", "entrar"] as const,
  todos: ["usuarios"] as const,
  lista: (
    cursor: string | undefined,
    filtro: Pick<ConsultaDeUsuarios, "busca" | "papel" | "situacao">,
  ) => ["usuarios", "lista", cursor ?? null, filtro] as const,
};

/** Sessao atual. `data === null` significa deslogado. */
export function useSessao() {
  const gateway = useUsuariosGateway();
  return useQuery({
    queryKey: chavesDeUsuarios.sessao,
    queryFn: () => gateway.sessaoAtual(),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useEntrar() {
  const gateway = useUsuariosGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationKey: chavesDeUsuarios.entrar,
    mutationFn: (credenciais: Credenciais) => gateway.entrar(credenciais),
    // Aqui, e nao no `mutate(..., { onSuccess })` do formulario: o formulario sai
    // da tela assim que o login comeca (entra a verificacao da sessao), e o React
    // Query nao chama o callback de quem ja foi desmontado.
    onSuccess: (sessao) => {
      lembrarSessaoConhecida();
      marcarLoginRecente();
      cliente.setQueryData(chavesDeUsuarios.sessao, sessao);
    },
  });
}

export function useSair() {
  const gateway = useUsuariosGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => gateway.sair(),
    // Mesmo se a chamada falhar (rede), o front esquece a sessao: o pior caso e
    // uma sessao que expira sozinha no servidor, nao uma tela que nao deixa sair.
    onSettled: () => {
      esquecerSessaoConhecida();
      cliente.clear();
      cliente.setQueryData(chavesDeUsuarios.sessao, null);
    },
  });
}

export function useEsqueciSenha() {
  const gateway = useUsuariosGateway();
  return useMutation({ mutationFn: (email: string) => gateway.esqueciSenha(email) });
}

export function useRedefinirSenha() {
  const gateway = useUsuariosGateway();
  return useMutation({ mutationFn: (dados: RedefinirSenha) => gateway.redefinirSenha(dados) });
}

export function useListarUsuarios(
  cursor: string | undefined,
  filtro: Pick<ConsultaDeUsuarios, "busca" | "papel" | "situacao"> = {},
) {
  const gateway = useUsuariosGateway();
  return useQuery({
    queryKey: chavesDeUsuarios.lista(cursor, filtro),
    queryFn: () => gateway.listar({ cursor, ...filtro }),
  });
}

function useInvalidarUsuarios() {
  const cliente = useQueryClient();
  return () => cliente.invalidateQueries({ queryKey: chavesDeUsuarios.todos });
}

export function useCriarUsuario() {
  const gateway = useUsuariosGateway();
  const invalidar = useInvalidarUsuarios();
  return useMutation({
    mutationFn: (dados: CriarUsuario) => gateway.criar(dados),
    onSuccess: () => invalidar(),
  });
}

export function useAtualizarUsuario() {
  const gateway = useUsuariosGateway();
  const invalidar = useInvalidarUsuarios();
  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: AtualizarUsuario }) =>
      gateway.atualizar(id, dados),
    onSuccess: () => invalidar(),
  });
}

export function useEnviarLinkDeSenha() {
  const gateway = useUsuariosGateway();
  return useMutation({ mutationFn: (id: string) => gateway.enviarLinkDeSenha(id) });
}

export function useExcluirUsuario() {
  const gateway = useUsuariosGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gateway.excluir(id),
    // Some tambem das listas de Pessoas dos clientes.
    onSuccess: () => cliente.invalidateQueries(),
  });
}

/** Salva o proprio perfil e atualiza a sessao (some o passo de primeiro acesso). */
export function useSalvarPerfil() {
  const gateway = useUsuariosGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (dados: AtualizarPerfil) => gateway.salvarPerfil(dados),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesDeUsuarios.sessao }),
  });
}
